import { BranchTag } from "@/components/BranchTag";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, FileMinus, Ban, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { partyLabel } from "@/lib/format";

import { confirmAction } from "@/lib/dialogs";
const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];
const REASONS = {
  RETURN: { label: "Goods returned", purchase: true, service: false },
  RATE_DIFFERENCE: { label: "Rate higher than agreed", purchase: true, service: true },
  SHORT_SUPPLY: { label: "Short supply / billed more than received", purchase: true, service: false },
  QUALITY: { label: "Poor quality / damage", purchase: true, service: true },
  PENALTY: { label: "Penalty (delay, safety, etc.)", purchase: true, service: true },
  OTHER: { label: "Other", purchase: true, service: true },
};
const AUTO_LABEL = { RATE_DIFFERENCE: "Auto · rate above PO", EXCESS_QTY: "Auto · billed more than received" };
const inr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DebitNotes() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const { companies } = useCompany() || {};
  const [notes, setNotes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [purchaseBills, setPurchaseBills] = useState([]);
  const [serviceBills, setServiceBills] = useState([]);
  const [grns, setGrns] = useState([]);
  const [pos, setPos] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const safe = (p) => p.catch(() => ({ data: [] }));
      const [noteRes, vendorRes, pbRes, sbRes, grnRes, poRes, whRes] = await Promise.all([
        api.get("/procurement/debit-notes"), api.get("/procurement/vendors"),
        safe(api.get("/procurement/purchase-bills")), safe(api.get("/procurement/service-bills")),
        safe(api.get("/procurement/grns")), safe(api.get("/procurement/purchase-orders")), safe(api.get("/inventory/warehouses")),
      ]);
      setNotes(noteRes.data); setVendors(vendorRes.data); setPurchaseBills(pbRes.data); setServiceBills(sbRes.data);
      setGrns(grnRes.data); setPos(poRes.data); setWarehouses(whRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";
  const billNumber = (n) => purchaseBills.find((b) => b.id === n.purchase_bill_id)?.number || serviceBills.find((b) => b.id === n.service_bill_id)?.number || "—";

  const act = async (note, action, message) => {
    setBusyId(note.id);
    try {
      if (action === "delete") await api.delete(`/procurement/debit-notes/${note.id}`);
      else await api.post(`/procurement/debit-notes/${note.id}/${action}`);
      toast.success(message);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const shown = pf.apply(notes, (n) => n.project_id);
  const drafts = shown.filter((n) => n.status === "DRAFT");

  return (
    <div className="space-y-6" data-testid="debit-notes-page">
      <PageHeader
        title="Debit Notes"
        description="Reduce what you owe a vendor on a bill — returns, rate or quantity differences, quality, penalties. Drafted automatically when a bill is over the PO rate or the GRN quantity."
        action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canManage && (
          <CreateDebitNoteDialog companies={companies} vendors={vendors} purchaseBills={purchaseBills} serviceBills={serviceBills} grns={grns} pos={pos} warehouses={warehouses} onCreated={load} />
        )}</div>}
      />
      {drafts.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400" data-testid="debit-notes-drafts-banner">
          {drafts.length} draft debit note(s) waiting — check each with the vendor, then Post (or delete if the vendor explained the difference).
        </div>
      )}
      <div className="rounded-xl border border-border bg-card">
        {!loading && shown.length === 0 ? (
          <EmptyState title="No debit notes" testId="debit-notes-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Number</TableHead><TableHead>Vendor</TableHead><TableHead>Against bill</TableHead><TableHead>Reason</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shown.map((n) => (
                <TableRow key={n.id} data-testid={`debit-note-row-${n.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">
                    {n.number}<BranchTag row={n} />
                    {n.auto_source && <div className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-1.5 py-0.5 font-sans text-[10px] text-sky-600 dark:text-sky-400"><Sparkles className="h-3 w-3" />{AUTO_LABEL[n.auto_source] || "Auto"}</div>}
                  </TableCell>
                  <TableCell className="text-foreground">{vendorName(n.vendor_id)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{billNumber(n)}{n.service_bill_id ? <span className="font-sans"> · service</span> : ""}</TableCell>
                  <TableCell className="max-w-sm text-xs text-muted-foreground"><div className="line-clamp-2">{n.reason || "—"}</div></TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    <div className="font-semibold text-foreground">{inr(n.amount)}</div>
                    {Number(n.tax_amount) > 0 && <div className="text-[10px] text-muted-foreground">incl. GST {inr(n.tax_amount)}</div>}
                  </TableCell>
                  <TableCell><StatusPill value={n.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1.5">
                      {canManage && n.status === "DRAFT" && (
                        <>
                          <Button size="sm" variant="outline" disabled={busyId === n.id} onClick={() => act(n, "post", `${n.number} posted — the bill's balance is reduced`)} data-testid={`post-debit-note-button-${n.number}`}>
                            <FileMinus className="h-3.5 w-3.5" /> Post
                          </Button>
                          <Button size="sm" variant="outline" disabled={busyId === n.id} title="Delete draft"
                            onClick={async () => await confirmAction(`Delete draft ${n.number}?`) && act(n, "delete", `${n.number} deleted`)} data-testid={`delete-debit-note-button-${n.number}`}>
                            <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                          </Button>
                        </>
                      )}
                      {canManage && n.status === "POSTED" && (
                        <Button size="sm" variant="outline" disabled={busyId === n.id} title="Cancel (reverses it)"
                          onClick={async () => await confirmAction(`Cancel ${n.number}? The GL entry is reversed and the bill's balance goes back up.`) && act(n, "cancel", `${n.number} cancelled`)} data-testid={`cancel-debit-note-button-${n.number}`}>
                          <Ban className="h-3.5 w-3.5 text-rose-400" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateDebitNoteDialog({ companies, vendors, purchaseBills, serviceBills, grns, pos, warehouses, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const EMPTY = { vendor_id: "", bill_key: "", reason: "RATE_DIFFERENCE", details: "", note_date: today, taxable: "", gst_pct: "", warehouse_id: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [returnQty, setReturnQty] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const bills = useMemo(() => [
    ...purchaseBills.filter((b) => b.vendor_id === form.vendor_id && b.status === "POSTED").map((b) => ({
      key: `pb:${b.id}`, kind: "PURCHASE", bill: b, due: Number(b.total_amount),
      gst: Number(b.subtotal) ? (Number(b.tax_amount) / Number(b.subtotal)) * 100 : 0,
    })),
    ...serviceBills.filter((b) => b.vendor_id === form.vendor_id && b.status === "POSTED").map((b) => ({
      key: `sb:${b.id}`, kind: "SERVICE", bill: b, due: Number(b.net_payable),
      gst: Number(b.subtotal) ? (Number(b.tax_amount) / Number(b.subtotal)) * 100 : 0,
    })),
  ].map((x) => ({ ...x, balance: Math.round((x.due - Number(x.bill.paid_amount || 0) - Number(x.bill.adjusted_amount || 0)) * 100) / 100 }))
    .filter((x) => x.balance > 0), [purchaseBills, serviceBills, form.vendor_id]);

  const chosen = bills.find((b) => b.key === form.bill_key);
  const reasonsForBill = Object.entries(REASONS).filter(([, r]) => !chosen || (chosen.kind === "SERVICE" ? r.service : r.purchase));
  const isReturn = form.reason === "RETURN" && chosen?.kind === "PURCHASE";

  // For returns: the GRN lines behind the bill, valued at the PO rate
  const grn = isReturn ? grns.find((g) => g.id === chosen.bill.grn_id) : null;
  const poLines = pos.flatMap((p) => p.items || []);
  const returnLines = (grn?.items || []).map((gi) => {
    const pl = poLines.find((l) => l.id === gi.po_item_id);
    return { id: gi.id, description: pl?.description || "Item", item_id: pl?.item_id || null, received: Number(gi.quantity_received), rate: Number(pl?.rate || 0) };
  });
  const returnTaxable = returnLines.reduce((s, l) => s + Number(returnQty[l.id] || 0) * l.rate, 0);

  const taxable = isReturn ? returnTaxable : Number(form.taxable || 0);
  const gstPct = Number(form.gst_pct || 0);
  const gst = Math.round(taxable * gstPct) / 100;
  const total = Math.round((taxable + gst) * 100) / 100;
  const problem = !chosen ? "" : total <= 0 ? "Enter an amount" : total > chosen.balance + 0.005 ? `Total ₹${inr(total)} is more than the bill's balance ₹${inr(chosen.balance)}` : "";

  const pickBill = (key) => {
    const b = bills.find((x) => x.key === key);
    const reasonOk = b && (b.kind === "SERVICE" ? REASONS[form.reason]?.service : REASONS[form.reason]?.purchase);
    setForm({ ...form, bill_key: key, gst_pct: b ? b.gst.toFixed(2) : "", reason: reasonOk ? form.reason : "OTHER" });
    setReturnQty({});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!chosen || problem) return;
    setSaving(true);
    try {
      const payload = {
        company_id: chosen.bill.company_id, vendor_id: form.vendor_id, note_date: form.note_date,
        purchase_bill_id: chosen.kind === "PURCHASE" ? chosen.bill.id : null,
        service_bill_id: chosen.kind === "SERVICE" ? chosen.bill.id : null,
        warehouse_id: isReturn ? form.warehouse_id || grn?.warehouse_id || null : null,
        amount: total.toFixed(2), tax_amount: gst.toFixed(2),
        reason: `${form.reason}: ${form.details || REASONS[form.reason].label}`,
        items: isReturn && (form.warehouse_id || grn?.warehouse_id)
          ? returnLines.filter((l) => Number(returnQty[l.id]) > 0).map((l) => ({
            item_id: l.item_id, description: l.description, quantity: returnQty[l.id], amount: (Number(returnQty[l.id]) * l.rate).toFixed(2),
          }))
          : [],
      };
      await api.post("/procurement/debit-notes", payload);
      toast.success("Debit note created — Post it to reduce the bill");
      setForm(EMPTY);
      setReturnQty({});
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-debit-note-button"><Plus className="h-4 w-4" /> New Debit Note</Button></DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto overflow-x-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>New Debit Note</DialogTitle>
          <DialogDescription>Reduces what you owe on one bill. Posting also reduces GST credit and the project's cost.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-debit-note-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Vendor</Label>
              <Select required value={form.vendor_id} onValueChange={(v) => { setForm({ ...form, vendor_id: v, bill_key: "" }); setReturnQty({}); }}>
                <SelectTrigger className="mt-1.5" data-testid="debit-note-vendor-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" value={form.note_date} onChange={set("note_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Bill</Label>
            <Select value={form.bill_key} onValueChange={pickBill} disabled={!form.vendor_id}>
              <SelectTrigger className="mt-1.5 [&>span]:truncate" data-testid="debit-note-bill-select"><SelectValue placeholder={form.vendor_id ? "Select the bill" : "Choose a vendor first"} /></SelectTrigger>
              <SelectContent>
                {bills.map((b) => <SelectItem key={b.key} value={b.key}>{b.bill.number} · {b.kind === "SERVICE" ? "Service" : "Purchase"} · balance ₹{inr(b.balance)}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.vendor_id && bills.length === 0 && <p className="mt-1 text-[11px] text-muted-foreground">This vendor has no posted bills with a balance.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{reasonsForBill.map(([k, r]) => <SelectItem key={k} value={k}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Details</Label><Input value={form.details} onChange={set("details")} placeholder="e.g. 20 modules cracked" className="mt-1.5" /></div>
          </div>

          {isReturn ? (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="grid grid-cols-2 gap-3">
                <p className="text-xs font-semibold text-foreground">Quantity returned (valued at PO rate)</p>
                <Select value={form.warehouse_id || grn?.warehouse_id || ""} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Return from warehouse" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {returnLines.length === 0 && <p className="text-xs text-muted-foreground">This bill has no GRN lines — use "Other" and enter an amount instead.</p>}
              {returnLines.map((l) => (
                <div key={l.id} className="flex items-center gap-2 text-sm">
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">{l.description} (received {l.received}, ₹{inr(l.rate)})</span>
                  <Input type="number" step="0.001" min="0" max={l.received} className="w-24" value={returnQty[l.id] || ""} onChange={(e) => setReturnQty({ ...returnQty, [l.id]: e.target.value })} />
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground">Stock items leave the warehouse when you Post.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount before GST (₹)</Label><Input type="number" step="0.01" min="0" value={form.taxable} onChange={set("taxable")} className="mt-1.5" data-testid="debit-note-taxable-input" /></div>
              <div><Label>GST %</Label><Input type="number" step="0.01" min="0" max="100" value={form.gst_pct} onChange={set("gst_pct")} className="mt-1.5" /></div>
            </div>
          )}
          {isReturn && (
            <div className="grid grid-cols-2 gap-3">
              <div className="text-sm text-muted-foreground">Before GST: <span className="font-mono text-foreground">₹{inr(taxable)}</span></div>
              <div><Label>GST %</Label><Input type="number" step="0.01" min="0" max="100" value={form.gst_pct} onChange={set("gst_pct")} className="mt-1.5" /></div>
            </div>
          )}

          <div className="rounded-lg bg-muted/50 p-3 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>GST</span><span className="font-mono">₹{inr(gst)}</span></div>
            <div className="flex justify-between font-semibold text-foreground"><span>Debit note total</span><span className="font-mono">₹{inr(total)}</span></div>
            {chosen && !problem && total > 0 && <div className="mt-1 text-[11px] text-muted-foreground">Bill balance after posting: ₹{inr(chosen.balance - total)}</div>}
          </div>
          {problem && <p className="rounded-lg bg-rose-500/10 p-2 text-xs text-rose-700 dark:text-rose-400">{problem}</p>}
          <DialogFooter>
            <Button type="submit" disabled={saving || !chosen || Boolean(problem)} data-testid="create-debit-note-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
